import { useEffect, useRef, useState, useCallback } from "react";
import {
  fetchStatusEmail,
  getEmailsLogs,
  resendEmail,
} from "../../api/emailsApi";
import PageLoader from "../../components/pageLoader";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import {
  FilterIcon,
  RefreshIcon,
  SearchIcon,
  StatusOnlineIcon,
  XIcon,
} from "@heroicons/react/outline";

const EmailsLogsSection = () => {
  const [limit, setLimit] = useState(10);
  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [status, setStatus] = useState([
    {
      label: 'Sent',
      value: 'sent',
    },
    {
      label: 'Delivered',
      value: 'delivered',
    },
    {
      label: 'Bounced',
      value: 'bounced',
    },
  ]);
  const [statusFilter, setStatusFilter] = useState("");

  const [resendTarget, setResendTarget] = useState(null);
  const [resendBackup, setResendBackup] = useState(false);
  const [resending, setResending] = useState(false);

  const [fetchStatusTarget, setFetchStatusTarget] = useState(null);
  const [fetchingStatus, setFetchingStatus] = useState(false);

  const [scrollState, setScrollState] = useState({ left: false, right: false });
  const wrapperRef = useRef(null);

  const totalPages = Math.ceil(total / limit);

  // ── Scroll detection ──
  const updateScrollState = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setScrollState((prev) =>
      prev.left === left && prev.right === right ? prev : { left, right },
    );
  }, []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    // Run after paint so DOM is fully rendered
    const raf = requestAnimationFrame(() => {
      updateScrollState();
    });
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [emails, updateScrollState]);

  // ── Pagination ──
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);
    range.push(1);
    if (left > 2) range.push("...");
    for (let i = left; i <= right; i++) range.push(i);
    if (right < totalPages - 1) range.push("...");
    if (totalPages > 1) range.push(totalPages);
    return range;
  };

  // ── Fetch ──
  const fetchEmailLogs = async () => {
    try {
      setLoading(true);
      setError("");
      const offset = (page - 1) * limit;
      const data = await getEmailsLogs(limit, offset, search, statusFilter);
      console.log('--', data);
      setEmails(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setError("Failed to load logs.");
    } finally {
      setLoading(false);
    }
  };

  const handleLimitChange = (e) => {
    setPage(1);
    setLimit(Number(e.target.value));
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  const handleResendConfirm = async () => {
    try {
      setResending(true);
      await resendEmail(resendTarget.id, {
        resend_with_backup_address: resendBackup,
      });
      setToast({ type: "success", message: "Email resent successfully." });
      setResendTarget(null);
      setResendBackup(false);
      fetchEmailLogs();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail ||
          err?.detail ||
          "Failed to resend email.",
      });
    } finally {
      setResending(false);
    }
  };

  const handleFetchStatusConfirm = async () => {
    try {
      setFetchingStatus(true);
      await fetchStatusEmail(fetchStatusTarget.id);
      setToast({ type: "success", message: "Status fetched successfully." });
      setFetchStatusTarget(null);
      fetchEmailLogs();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail ||
          err?.detail ||
          "Failed to fetch status.",
      });
    } finally {
      setFetchingStatus(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 2000);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchEmailLogs();
  }, [page, limit, search, statusFilter]);

  // ── Status badge ──
  const statusStyle = (status) => {
    switch (status) {
      case "delivered":
        return "bg-emerald-500/10 border-emerald-500/20 text-emerald-400";
      case "sent":
        return "bg-blue-500/10 border-blue-500/20 text-blue-400";
      case "bounced":
        return "bg-red-500/10 border-red-500/20 text-red-400";
      case "failed":
        return "bg-red-500/10 border-red-500/20 text-red-400";
      default:
        return "bg-slate-500/10 border-slate-500/20 text-slate-400";
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="grid grid-cols-1 gap-4">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-white">Manage email logs.</p>
          {total > 0 && (
            <span className="text-xs text-slate-500">{total} total logs</span>
          )}
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
          {error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by order ID, name or email..."
            className="w-full bg-slate-900/60 border border-white/10 rounded-lg pl-9 pr-9 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500/40"
          />
          {searchInput && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition"
            >
              <XIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Event filter */}
        <div className="relative sm:w-64">
          <FilterIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
          <select
            value={statusFilter}
            onChange={handleStatusFilter}
            className="w-full bg-slate-900/60 border border-white/10 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40 cursor-pointer appearance-none"
          >
            <option value="">All Status</option>
            {status.map((st) => (
              <option key={st.value} value={st.value}>
                {st.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Table card ── */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl overflow-visible">
        {/* Scroll container with fade shadows */}
        <div className="relative">
          {/* Left shadow */}
          {scrollState.left && (
            <div
              className="absolute left-0 top-0 bottom-0 w-10 z-10 pointer-events-none"
              style={{
                background:
                  "linear-gradient(to right, rgba(10,18,35,0.9), transparent)",
              }}
            />
          )}

          {/* Right shadow */}
          {scrollState.right && (
            <div
              className="absolute right-0 top-0 bottom-0 w-14 z-10 pointer-events-none"
              style={{
                background:
                  "linear-gradient(to left, rgba(10,18,35,0.95), transparent)",
              }}
            />
          )}

          {/* Scrollable area */}
          <div
            ref={wrapperRef}
            className="w-full max-w-full overflow-x-auto overflow-y-hidden"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(255,255,255,0.12) transparent",
            }}
          >
            <table className="border-collapse min-w-max w-full">
              <thead>
                <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-center text-slate-400">
                  <th className="px-4 py-3 whitespace-nowrap">Customer</th>
                  <th className="px-4 py-3 whitespace-nowrap">Order</th>
                  <th className="px-4 py-3 whitespace-nowrap">Recipient</th>
                  <th className="px-4 py-3  whitespace-nowrap">Type</th>
                  <th className="px-4 py-3 whitespace-nowrap">Sender</th>
                  <th className="px-4 py-3  whitespace-nowrap">Status</th>
                  <th className="px-4 py-3  whitespace-nowrap">Subject</th>
                  <th className="px-4 py-3 whitespace-nowrap">Reason</th>
                  <th className="px-4 py-3  whitespace-nowrap">Time</th>
                  <th className="px-4 py-3 whitespace-nowrap">Actions</th>
                </tr>
              </thead>

              <tbody>
                {emails.map((em) => (
                  <tr
                    key={em.id}
                    className="border-b border-white/5 text-center last:border-none hover:bg-slate-800/40 transition"
                  >
                    <td className="px-4 py-3 text-sm text-white whitespace-nowrap">
                      {em.customer_name || "—"}
                    </td>

                    <td className="px-4 py-3 text-sm font-mono text-slate-300 whitespace-nowrap">
                      {em.order_number || "—"}
                    </td>

                    <td className="px-4 py-3 text-sm text-slate-300">
                      <div className="truncate max-w-[180px]">
                        {em.recipient || "—"}
                      </div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-slate-800 border border-white/10 text-slate-300">
                        {em.email_type || "—"}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-sm text-slate-300 whitespace-nowrap">
                      {em.sender || "—"}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 rounded-md text-xs font-medium border ${statusStyle(em.status)}`}
                      >
                        {em.status || "—"}
                      </span>
                    </td>

                    {/* Subject */}
                    <td className="px-4 py-3 text-sm text-slate-400">
                      {em.subject ? (
                        <div className="relative group inline-block max-w-[120px]">
                          <span className="truncate block cursor-help underline underline-offset-2 decoration-dashed decoration-slate-600">
                            {em.subject}
                          </span>
                          <div className="absolute z-50 bottom-full left-0 mb-2 w-72 bg-slate-800 border border-white/10 text-xs text-slate-300 rounded-lg px-3 py-2.5 opacity-0 group-hover:opacity-100 transition pointer-events-none">
                            {em.subject}
                            <div className="absolute top-full left-4 border-4 border-transparent border-t-slate-800" />
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>

                    {/* Reason */}
                    <td className="px-4 py-3 text-sm text-slate-400">
                      {em.bounce_reason ? (
                        <div className="relative group inline-block max-w-[120px]">
                          <span className="truncate block cursor-help underline underline-offset-2 decoration-dashed decoration-slate-600">
                            {em.bounce_reason}
                          </span>
                          <div className="absolute z-50 bottom-full left-0 mb-2 w-72 bg-slate-800 border border-white/10 text-xs text-slate-300 rounded-lg px-3 py-2.5 opacity-0 group-hover:opacity-100 transition pointer-events-none">
                            {em.bounce_reason}
                            <div className="absolute top-full left-4 border-4 border-transparent border-t-slate-800" />
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-xs text-slate-400">
                      {em.created_at
                        ? new Date(em.created_at).toLocaleString()
                        : "—"}
                    </td>

                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => {
                            setResendTarget(em);
                            setResendBackup(false);
                          }}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-white/10 text-slate-400 hover:text-white hover:bg-slate-700 transition"
                          title="Resend"
                        >
                          <RefreshIcon className="w-3.5 h-3.5" />
                          Resend
                        </button>
                        <button
                          onClick={() => setFetchStatusTarget(em)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-white/10 text-slate-400 hover:text-white hover:bg-slate-700 transition"
                          title="Fetch Status"
                        >
                          <StatusOnlineIcon className="w-3.5 h-3.5" />
                          Fetch Status
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Empty state */}
        {emails.length === 0 && (
          <div className="py-10 text-center text-slate-400 text-sm">
            No logs found.
          </div>
        )}

        {/* Pagination */}
        <div className="flex justify-end">
          <div className="flex items-center gap-3 px-6 py-4 text-sm text-slate-400">
            <span className="text-slate-400">Rows per page:</span>
            <select
              value={limit}
              onChange={handleLimitChange}
              className="bg-slate-800 text-slate-300 border border-white/10 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-pink-500 cursor-pointer"
            >
              {[5, 10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>

            {totalPages > 1 && (
              <>
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition ${
                    page === 1
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-800 text-white"
                  }`}
                >
                  Previous
                </button>

                <div className="flex gap-1">
                  {getPageNumbers().map((pageNumber, index) =>
                    pageNumber === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-3 py-1 text-slate-500 select-none"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageNumber}
                        onClick={() => setPage(pageNumber)}
                        className={`px-3 py-1 rounded-md border border-white/10 transition ${
                          page === pageNumber
                            ? "bg-pink-600 text-white"
                            : "text-slate-300 hover:bg-slate-800 hover:text-white"
                        }`}
                      >
                        {pageNumber}
                      </button>
                    ),
                  )}
                </div>

                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition ${
                    page === totalPages
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-800 text-white"
                  }`}
                >
                  Next
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Resend Modal ── */}
      {!!resendTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-xl shadow-xl">
            <div className="px-6 py-4 border-b border-white/10">
              <h2 className="text-white font-semibold text-sm">Resend Email</h2>
              <p className="text-xs text-slate-500 mt-0.5 font-mono">
                {resendTarget.order_number}
              </p>
            </div>

            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-slate-400">
                Are you sure you want to resend this email? The email will be
                sent again to the original recipient.
              </p>

              {/* Backup address checkbox */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div
                  onClick={() => setResendBackup((v) => !v)}
                  className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition ${
                    resendBackup
                      ? "bg-pink-600 border-pink-500"
                      : "bg-slate-800 border-white/20 group-hover:border-white/40"
                  }`}
                >
                  {resendBackup && (
                    <svg
                      className="w-2.5 h-2.5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
                <div onClick={() => setResendBackup((v) => !v)}>
                  <p className="text-sm text-slate-300 font-medium">
                    Resend with backup address
                  </p>
                </div>
              </label>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button
                onClick={() => {
                  setResendTarget(null);
                  setResendBackup(false);
                }}
                className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleResendConfirm}
                disabled={resending}
                className="px-4 py-2 text-sm rounded-lg text-white bg-pink-600 hover:bg-pink-700 disabled:opacity-50 transition flex items-center gap-2"
              >
                {resending ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Confirm Resend"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Fetch Status Modal ── */}
      {!!fetchStatusTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-xl shadow-xl">
            <div className="px-6 py-4 border-b border-white/10">
              <h2 className="text-white font-semibold text-sm">
                Fetch Email Status
              </h2>
              <p className="text-xs text-slate-500 mt-0.5 font-mono">
                {fetchStatusTarget.order_number}
              </p>
            </div>

            <div className="px-6 py-5">
              <p className="text-sm text-slate-400">
                This will fetch the latest delivery status for this email from
                the mail provider. The status in the log will be updated
                accordingly.
              </p>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button
                onClick={() => setFetchStatusTarget(null)}
                className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleFetchStatusConfirm}
                disabled={fetchingStatus}
                className="px-4 py-2 text-sm rounded-lg text-white bg-pink-600 hover:bg-pink-700 disabled:opacity-50 transition flex items-center gap-2"
              >
                {fetchingStatus ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    Fetching...
                  </>
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

export default EmailsLogsSection;
